import qz from 'qz-tray';
import { KEYUTIL, KJUR, hextob64 } from 'jsrsasign';

/**
 * One QZ Tray connection for the whole app.
 *
 * What this replaces: every print used to register the security promises
 * again, open a fresh WebSocket, print, and disconnect. That is three
 * handshakes per receipt, and with kitchen tickets it would be three per
 * ticket as well. The socket is opened once and kept.
 *
 * QZ reaches two kinds of printer and this module hides the difference:
 *   • the USB receipt printer, addressed by its Windows name
 *   • a station printer on the shop LAN, addressed by host and port 9100
 *
 * ── On the signing ──────────────────────────────────────────────────────
 * The certificate and key are fetched from the app's own origin, unchanged
 * from before. That means the private key is downloadable by anyone who
 * visits the site, and once `override.crt` is installed on a till, a page
 * open in that browser could print on it. Moving the signature to a Netlify
 * function is a small job and worth doing; it is deliberately not part of
 * this change.
 */

let securityReady = false;

/**
 * The connection attempt that is in flight, if any.
 *
 * Shared so two tickets fired at the same moment cannot each start their own
 * handshake — QZ answers the second with "connection already open" and the
 * print is lost.
 */
let attempt = null;

/**
 * How long a single attempt may take before it is called dead.
 *
 * A till with QZ Tray running answers in well under a tenth of a second, so
 * this never touches a working shop. On a till without it, qz-tray works
 * through a list of hosts and ports — localhost AND localhost.qz.io, four
 * ports each, secure then not — and every localhost.qz.io attempt waits on a
 * DNS lookup that a shop with no internet cannot answer. That is what used to
 * leave the cashier watching "Placing…" for half a minute.
 */
const CONNECT_TIMEOUT_MS = 2500;

/**
 * How long a dead QZ is remembered.
 *
 * Without this, an order with three kitchen stations and a receipt discovers
 * the same absence four times over. Short on purpose: a printer switched on
 * again is picked up within half a minute at worst, and any successful
 * connection clears it at once.
 */
const COLD_FOR_MS = 30000;

let coldUntil = 0;

/** Worded so humaniseError below turns it into the sentence a cashier reads. */
const NOT_RUNNING = 'QZ Tray is not running on this computer.';

/**
 * Forget a recent failure and try the next connection for real.
 *
 * For the moment somebody has gone and fixed the printer and wants to know
 * whether it worked — the test slip in Administration calls this, so a test
 * never answers from memory.
 */
export function forgetPrinterTrouble() {
  coldUntil = 0;
}

/** Registered once. Calling these repeatedly is what the old code did. */
function setupSecurity() {
  if (securityReady) return;
  securityReady = true;

  qz.security.setCertificatePromise((resolve, reject) => {
    fetch('/qz/digital-certificate.txt', { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Could not load the QZ certificate');
        return response.text();
      })
      .then(resolve)
      .catch(reject);
  });

  qz.security.setSignatureAlgorithm('SHA512');

  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    fetch('/qz/private-key.pem', { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Could not load the QZ key');
        return response.text();
      })
      .then((privateKey) => {
        const key = KEYUTIL.getKey(privateKey);
        const signature = new KJUR.crypto.Signature({ alg: 'SHA512withRSA' });
        signature.init(key);
        signature.updateString(toSign);
        resolve(hextob64(signature.sign()));
      })
      .catch(reject);
  });
}

/** The handshake itself, started at most once at a time. */
function openSocket() {
  if (attempt) return attempt;

  const mine = qz.websocket.connect().then(
    () => {
      // Only clear the slot if it is still this attempt's. An abandoned one
      // settling late must not wipe a newer handshake that is under way.
      if (attempt === mine) attempt = null;
      coldUntil = 0;
    },
    (error) => {
      // A failed attempt must not be remembered as "still connecting".
      if (attempt === mine) attempt = null;
      throw error;
    }
  );

  attempt = mine;

  // The waiter below walks away from this promise when it times out, so it is
  // observed here too: a late rejection nobody is listening to shows up in the
  // console as an unhandled one.
  mine.catch(() => {});

  return mine;
}

/**
 * Connect, or hand back the connection already open.
 *
 * Two things were added here because of how long a MISSING QZ Tray takes to
 * announce itself: the attempt is given a deadline, and a failure is
 * remembered for a moment so the rest of the same order does not each wait
 * for it again.
 *
 * Note what the deadline does NOT do: it does not cancel the handshake. QZ
 * carries on trying in the background, and if it succeeds a second later the
 * socket is simply open — `isActive()` above sees it and the next ticket goes
 * out normally. All the timeout does is stop the cashier waiting.
 */
export async function ensureConnected() {
  setupSecurity();

  if (isConnected()) {
    coldUntil = 0;
    return;
  }

  // Answered from memory: QZ was not there a moment ago and nothing since then
  // can have changed that.
  if (coldUntil && Date.now() < coldUntil) throw new Error(NOT_RUNNING);

  const socket = openSocket();
  let timer = null;

  try {
    await Promise.race([
      socket,
      new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(NOT_RUNNING + ' It did not answer.')),
          CONNECT_TIMEOUT_MS
        );
      }),
    ]);
    coldUntil = 0;
  } catch (error) {
    // The attempt we gave up on is ABANDONED, not cancelled — QZ carries on
    // with it in the background. Letting go of it here is what lets the next
    // order start a real handshake: held on to, a promise that never settles
    // would be raced again and again and a printer switched back on would
    // never be found.
    if (attempt === socket) attempt = null;

    coldUntil = Date.now() + COLD_FOR_MS;
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function isConnected() {
  try {
    return qz.websocket.isActive();
  } catch (error) {
    return false;
  }
}

/**
 * A queue per physical machine.
 *
 * Most thermal printers accept a single connection at a time, so two
 * documents aimed at the same address must go one after the other — sent
 * together, one is refused or silently discarded. Different machines run in
 * parallel; the kitchen should not wait for the bar.
 */
const queues = new Map();

/** Long enough for a printer to finish cutting and release the socket. */
const GAP_MS = 700;

const keyOf = (target) =>
  target.host ? `${target.host}:${target.port || 9100}` : `name:${target.name}`;

function enqueue(target, job) {
  const key = keyOf(target);
  const previous = queues.get(key) || Promise.resolve();

  // The tail always resolves, so one failed job cannot block the queue behind
  // it; the caller still sees its own rejection.
  const next = previous
    .catch(() => {})
    .then(() => job())
    .then(
      (value) => new Promise((done) => setTimeout(() => done(value), GAP_MS)),
      (error) =>
        new Promise((_, fail) => setTimeout(() => fail(error), GAP_MS))
    );

  queues.set(
    key,
    next.catch(() => {})
  );

  return next;
}

/**
 * Send raw ESC/POS to a printer.
 *
 * @param {object} target  `{ name }` for a Windows printer, or
 *                         `{ host, port }` for one on the network
 * @param {string} data    the ESC/POS string
 */
export function printRaw(target, data) {
  return enqueue(target, async () => {
    await ensureConnected();

    const config = target.host
      ? qz.configs.create({ host: target.host, port: Number(target.port) || 9100 })
      : qz.configs.create(target.name);

    return qz.print(config, [
      { type: 'raw', format: 'command', flavor: 'plain', data },
    ]);
  });
}

/**
 * Something a person can act on.
 *
 * QZ's own errors say things like "Unable to establish connection with QZ",
 * which tells a cashier nothing about which machine to go and look at.
 */
export function humaniseError(error, target) {
  const message = String(error?.message || error || '');

  if (/websocket|connection with qz|not running/i.test(message)) {
    return 'QZ Tray is not running on this computer.';
  }
  if (/unable to|refused|timed out|timeout/i.test(message)) {
    return target && target.host
      ? 'Printer is not responding. Check it is switched on and on the same network.'
      : 'Printer is not responding. Check it is switched on and connected.';
  }
  if (/printer/i.test(message) && /not found|invalid/i.test(message)) {
    return target && target.name
      ? `No printer named "${target.name}" on this computer.`
      : 'That printer could not be found.';
  }
  return message || 'Could not reach the printer.';
}
